import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { TeamPlayer } from "../../types";
import { useTranslation } from "react-i18next";
import Alert from "../Alert";
import SheetImageViewer from "./SheetImageViewer";
import SheetRowsTable from "./SheetRowsTable";
import KitColorPicker from "./KitColorPicker";
import {
  MATCH_SHEET_IMPORT_IMAGE_COUNT,
  MatchSheetImportApiError,
  extractMatchSheets,
  matchSheetPlayers,
  validateMatchSheetImage,
  type SheetField,
} from "../../lib/matchSheetImportApi";
import {
  KIT_PARTS,
  applyTeamChange,
  buildWizardFill,
  clearReviewState,
  computeConflicts,
  createReviewState,
  duplicateNumberKeys,
  duplicatePlayerGroups,
  effectiveKitHex,
  sheetRosterClashes,
  loadReviewState,
  resolveHeader,
  rowMatchInput,
  saveReviewState,
  updateRow,
  fullName,
  type ReviewRowState,
  type RosterContext,
  type RosterEntry,
  type RowSection,
  type ReviewState,
  type RowEdits,
  type RowResolution,
  type WizardFill,
} from "../../lib/matchSheetImportReview";
import {
  ProvisionalCreateError,
  RosterLoadError,
} from "../../lib/matchSheetImportApply";

interface TeamOption {
  team_id: string;
  name: string;
}
interface VenueOption {
  venue_id: string;
  name: string;
  city?: string;
}
interface CompetitionOption {
  competition_id: string | number;
  name: string;
}

interface Props {
  teams: TeamOption[];
  venues: VenueOption[];
  competitions: CompetitionOption[];
  onCancel: () => void;
  /** Throws on failure; never returns [] for an error. */
  fetchRoster: (teamId: string) => Promise<TeamPlayer[]>;
  /** Receives only resolved values (buildWizardFill throws otherwise). */
  onContinue: (
    fill: WizardFill,
    opts: {
      onCreated: (sheetIndex: 0 | 1, rowKey: string, playerId: string) => void;
    },
  ) => Promise<void>;
}

type RosterState = { entries?: RosterEntry[]; error?: boolean };

interface ContinueError {
  message: string;
  sheetIndex?: 0 | 1;
  rowKey?: string;
}

const raw = (f: SheetField): string =>
  f.value == null || f.value === "" ? "-" : String(f.value);

export default function MatchSheetImportStep({
  teams,
  venues,
  competitions,
  onCancel,
  fetchRoster,
  onContinue,
}: Props) {
  const { t } = useTranslation("admin");

  const [review, setReview] = useState<ReviewState | null>(() =>
    loadReviewState(),
  );
  const restoredFromSession = useRef(review !== null);
  const [files, setFiles] = useState<(File | null)[]>([null, null]);
  const [urls, setUrls] = useState<(string | null)[]>([null, null]);
  const [extracting, setExtracting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Keyed by `${sheet}:${rowKey}` so two sheets never share an entry. */
  const [rematching, setRematching] = useState<Set<string>>(new Set());
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});
  const [continuing, setContinuing] = useState(false);
  const [continueError, setContinueError] = useState<ContinueError | null>(
    null,
  );
  const [rosterState, setRosterState] = useState<Record<string, RosterState>>(
    {},
  );
  const rosterRequested = useRef<Set<string>>(new Set());
  const matchedNames = useRef<Record<string, string>>({});
  const matchSeq = useRef<Record<string, number>>({});
  const teamSeq = useRef<Record<number, number>>({});
  const continueErrRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const next = files.map((f) => (f ? URL.createObjectURL(f) : null));
    setUrls(next);
    return () => {
      next.forEach((u) => u && URL.revokeObjectURL(u));
    };
  }, [files]);

  useEffect(() => {
    if (review) saveReviewState(review);
  }, [review]);

  // ── rosters (D1) ────────────────────────────────────────────────────────

  const loadRoster = useCallback(
    (teamId: string) => {
      rosterRequested.current.add(teamId);
      setRosterState((prev) => ({ ...prev, [teamId]: {} }));
      fetchRoster(teamId)
        .then((entries) =>
          setRosterState((prev) => ({ ...prev, [teamId]: { entries } })),
        )
        .catch(() =>
          setRosterState((prev) => ({ ...prev, [teamId]: { error: true } })),
        );
    },
    [fetchRoster],
  );

  const t0 = review?.sheets[0].teamId ?? null;
  const t1 = review?.sheets[1].teamId ?? null;
  useEffect(() => {
    [t0, t1].forEach((id) => {
      if (id && !rosterRequested.current.has(id)) loadRoster(id);
    });
  }, [t0, t1, loadRoster]);

  const retryRoster = (teamId: string) => loadRoster(teamId);

  const rosterCtx = useMemo<RosterContext | undefined>(() => {
    if (!review) return undefined;
    const pick = (i: 0 | 1) => {
      const id = review.sheets[i].teamId;
      return id ? rosterState[id] : undefined;
    };
    return {
      rosters: [pick(0)?.entries ?? null, pick(1)?.entries ?? null],
      failed: [!!pick(0)?.error, !!pick(1)?.error],
    };
  }, [review, rosterState]);

  // ── upload ──────────────────────────────────────────────────────────────

  const setSlot = (idx: number, file: File | null) => {
    if (file) {
      const kind = validateMatchSheetImage(file);
      if (kind === "type") {
        setError(t("matchSheetImport.errorImageType"));
        return;
      }
      if (kind === "size") {
        setError(t("matchSheetImport.errorImageSize"));
        return;
      }
    }
    setError(null);
    setFiles((prev) => prev.map((f, i) => (i === idx ? file : f)));
  };

  const describeError = (err: unknown): string => {
    if (err instanceof MatchSheetImportApiError) {
      if (err.code === "ai_feature_disabled")
        return t("matchSheetImport.errorDisabled");
      if (err.status === 429) return t("matchSheetImport.errorRateLimit");
      if (err.status === 413) return t("matchSheetImport.errorImageSize");
      if (err.status === 415) return t("matchSheetImport.errorImageType");
      if (err.status === 503 || err.status === 422)
        return t("matchSheetImport.errorUnavailable");
      return err.message || t("matchSheetImport.errorGeneric");
    }
    return t("matchSheetImport.errorGeneric");
  };

  const handleExtract = async () => {
    if (files.some((f) => !f) || extracting) return;
    setExtracting(true);
    setError(null);
    try {
      const extraction = await extractMatchSheets(files as File[]);
      matchedNames.current = {};
      rosterRequested.current = new Set();
      setRosterState({});
      setContinueError(null);
      setRowErrors({});
      setReview(createReviewState(extraction));
    } catch (err) {
      setError(describeError(err));
    } finally {
      setExtracting(false);
    }
  };

  const handleCancel = () => {
    clearReviewState();
    onCancel();
  };

  // ── review mutations ────────────────────────────────────────────────────

  const sheetRows = (idx: 0 | 1) => review?.sheets[idx].rows ?? [];

  const rematchRows = useCallback(
    async (teamId: string, rows: ReviewRowState[]) => {
      const live = rows.filter((r) => r.resolution.kind !== "removed");
      if (live.length === 0) return {};
      const res = await matchSheetPlayers(teamId, live.map(rowMatchInput));
      const out: Record<number, ReviewRowState["playerMatch"]> = {};
      res.forEach((r) => {
        out[r.row] = r.player_match;
      });
      return out;
    },
    [],
  );

  const handleTeamChange = async (idx: 0 | 1, teamId: string) => {
    if (!review) return;
    const next = teamId || null;
    setError(null);
    const seq = (teamSeq.current[idx] = (teamSeq.current[idx] ?? 0) + 1);
    let matches: Record<number, ReviewRowState["playerMatch"]> | null = null;
    if (next) {
      try {
        matches = await rematchRows(next, sheetRows(idx));
      } catch (err) {
        if (teamSeq.current[idx] === seq) setError(describeError(err));
        return;
      }
    }
    if (teamSeq.current[idx] !== seq) return;
    setReview((prev) =>
      prev ? applyTeamChange(prev, idx, next, matches) : prev,
    );
  };

  const handleEdit = (idx: 0 | 1, key: string, edits: RowEdits) =>
    setReview((prev) => (prev ? updateRow(prev, idx, key, { edits }) : prev));

  // `pending` carries an edit made in the same tick as the commit (a reading
  // pick), which the `review` closure has not seen yet.
  const handleNameCommit = async (
    idx: 0 | 1,
    key: string,
    pending?: RowEdits,
  ) => {
    const state = review;
    if (!state) return;
    const sheet = state.sheets[idx];
    const found = sheet.rows.find((r) => r.key === key);
    if (!found || !sheet.teamId) return;
    const row = pending
      ? { ...found, edits: { ...found.edits, ...pending } }
      : found;
    const name = fullName(row);
    const memo = `${idx}-${key}`;
    if (matchedNames.current[memo] === undefined) {
      matchedNames.current[memo] = fullName({
        ...row,
        edits: {},
      });
    }
    if (matchedNames.current[memo] === name) return;
    // Newest call wins: a stale reply (or its failure) is ignored.
    const seq = (matchSeq.current[memo] = (matchSeq.current[memo] ?? 0) + 1);
    const rkey = `${idx}:${key}`;
    setRematching((prev) => new Set(prev).add(rkey));
    setRowErrors((prev) => ({ ...prev, [rkey]: "" }));
    try {
      const res = await matchSheetPlayers(sheet.teamId, [rowMatchInput(row)]);
      if (matchSeq.current[memo] !== seq) return;
      // memoised only after success, so a failed re-match can be retried
      matchedNames.current[memo] = name;
      const pm = res.find((r) => r.row === row.source.row)?.player_match;
      if (pm) {
        setReview((prev) =>
          prev
            ? updateRow(prev, idx, key, {
                playerMatch: pm,
                resolution: { kind: "none" },
                createdPlayerId: undefined,
              })
            : prev,
        );
      }
    } catch (err) {
      if (matchSeq.current[memo] === seq) {
        setRowErrors((prev) => ({ ...prev, [rkey]: describeError(err) }));
      }
    } finally {
      if (matchSeq.current[memo] === seq) {
        setRematching((prev) => {
          const n = new Set(prev);
          n.delete(rkey);
          return n;
        });
      }
    }
  };

  const handleResolve = (
    idx: 0 | 1,
    key: string,
    resolution: RowResolution,
  ) => {
    setContinueError(null);
    setReview((prev) =>
      prev
        ? updateRow(prev, idx, key, {
            resolution,
            ...(resolution.kind === "create"
              ? {}
              : { createdPlayerId: undefined }),
          })
        : prev,
    );
  };

  const handleSection = (idx: 0 | 1, key: string, section: RowSection) =>
    setReview((prev) => (prev ? updateRow(prev, idx, key, { section }) : prev));

  const conflicts = useMemo(
    () => (review ? computeConflicts(review, rosterCtx) : []),
    [review, rosterCtx],
  );

  const handleContinue = async () => {
    if (!review || conflicts.length > 0 || continuing) return;
    setContinuing(true);
    setError(null);
    setContinueError(null);
    try {
      await onContinue(buildWizardFill(review, rosterCtx), {
        onCreated: (sheetIndex, rowKey, playerId) =>
          setReview((prev) =>
            prev
              ? updateRow(prev, sheetIndex, rowKey, {
                  createdPlayerId: playerId,
                })
              : prev,
          ),
      });
      clearReviewState();
    } catch (err) {
      if (err instanceof ProvisionalCreateError) {
        const i = err.info;
        const message =
          i.kind === "clash"
            ? t("matchSheetImport.clash", { number: i.number, name: i.name })
            : i.kind === "duplicate"
              ? t("matchSheetImport.duplicateRow", { name: i.name })
              : err.message || t("matchSheetImport.errorContinue");
        setContinueError({
          message,
          sheetIndex: err.sheetIndex,
          rowKey: err.rowKey,
        });
      } else if (err instanceof RosterLoadError) {
        setContinueError({
          message: t("matchSheetImport.rosterLoadFailed"),
        });
      } else {
        setContinueError({
          message:
            (err as Error)?.message || t("matchSheetImport.errorContinue"),
        });
      }
    } finally {
      setContinuing(false);
    }
  };

  // Bring the failing row's error (or the Continue error) into view.
  useEffect(() => {
    if (!continueError) return;
    const target = continueError.rowKey
      ? document.querySelector(
          `[data-testid="sheet-row-${continueError.sheetIndex}-${continueError.rowKey}-create-error"]`,
        )
      : continueErrRef.current;
    (target as HTMLElement | null)?.scrollIntoView?.({
      block: "center",
      behavior: "smooth",
    });
  }, [continueError]);

  // ── render: upload ──────────────────────────────────────────────────────

  if (!review) {
    return (
      <div className="space-y-4" data-testid="sheet-import-upload">
        <h3 className="text-lg font-semibold">
          {t("matchSheetImport.uploadTitle")}
        </h3>
        <p className="text-sm text-gray-600">
          {t("matchSheetImport.uploadHint")}
        </p>
        {error && (
          <Alert tone="error" onDismiss={() => setError(null)}>
            <span data-testid="sheet-import-error">{error}</span>
          </Alert>
        )}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {Array.from({ length: MATCH_SHEET_IMPORT_IMAGE_COUNT }).map(
            (_, i) => (
              <div
                key={i}
                className="rounded border-2 border-dashed border-gray-300 p-3"
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  const f = e.dataTransfer.files?.[0];
                  if (f) setSlot(i, f);
                }}
              >
                <label
                  className="block text-sm font-medium mb-1"
                  htmlFor={`sheet-import-file-${i}`}
                >
                  {t("matchSheetImport.sheetN", { n: i + 1 })}
                </label>
                <input
                  id={`sheet-import-file-${i}`}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  disabled={extracting}
                  data-testid={`sheet-import-file-${i}`}
                  onChange={(e) => setSlot(i, e.target.files?.[0] ?? null)}
                />
                {urls[i] && (
                  <img
                    src={urls[i] as string}
                    alt={t("matchSheetImport.sheetN", { n: i + 1 })}
                    className="mt-2 max-h-48"
                  />
                )}
              </div>
            ),
          )}
        </div>
        {extracting && (
          <div
            className="flex items-center gap-3 text-sm text-gray-700"
            role="status"
            data-testid="sheet-import-extracting"
          >
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
            {t("matchSheetImport.extracting")}
          </div>
        )}
        <div className="flex justify-between">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleCancel}
            disabled={extracting}
            data-testid="sheet-import-cancel"
          >
            {t("cancel")}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleExtract}
            disabled={files.some((f) => !f) || extracting}
            data-testid="sheet-import-extract-btn"
          >
            {t("matchSheetImport.extract")}
          </button>
        </div>
      </div>
    );
  }

  // ── render: review ──────────────────────────────────────────────────────

  const ex = review.extraction;
  const header = resolveHeader(review);
  const conflictIds = new Set(conflicts.map((c) => c.id));
  const hasConflict = (id: string) => conflictIds.has(id);
  const warn = (id: string) =>
    hasConflict(id) ? "border-red-500 bg-red-50" : "";
  const teamName = (id: string | null) =>
    teams.find((x) => x.team_id === id)?.name ?? "-";

  const setHeader = (patch: ReviewState["header"]) =>
    setReview((prev) =>
      prev ? { ...prev, header: { ...prev.header, ...patch } } : prev,
    );

  const crossValues = (field: "date" | "time" | "venue") =>
    ex.cross_sheet.find((c) => c.field === field)?.values ?? null;

  const sheetFields = (pick: (s: (typeof ex.sheets)[number]) => SheetField) =>
    ex.sheets.map((s, i) => (
      <span key={i} className="mr-3 text-xs text-gray-500">
        {t("matchSheetImport.sheetN", { n: i + 1 })}: {raw(pick(s))}
        {pick(s).status !== "ok"
          ? ` (${t(`matchSheetImport.chip.${pick(s).status}`)})`
          : ""}
      </span>
    ));

  return (
    <div className="space-y-6" data-testid="sheet-import-review">
      {restoredFromSession.current && files.every((f) => !f) && (
        <Alert tone="info">{t("matchSheetImport.restoredHint")}</Alert>
      )}
      {error && (
        <Alert tone="error" onDismiss={() => setError(null)}>
          <span data-testid="sheet-import-error">{error}</span>
        </Alert>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {[0, 1].map((i) => (
          <SheetImageViewer
            key={i}
            url={urls[i]}
            label={t("matchSheetImport.sheetN", { n: i + 1 })}
            testId={`sheet-image-${i}`}
            onAttach={(f) => setSlot(i, f)}
          />
        ))}
      </div>

      {ex.sheets.flatMap((s, i) =>
        s.warnings.map((w, j) => (
          <Alert key={`${i}-${j}`} tone="warning">
            {t("matchSheetImport.sheetN", { n: i + 1 })}: {w}
          </Alert>
        )),
      )}

      {/* Match details */}
      <section
        className="space-y-3 rounded border p-4"
        data-testid="sheet-details"
      >
        <h4 className="font-semibold">{t("matchSheetImport.detailsTitle")}</h4>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div>
            <label className="block text-sm font-medium" htmlFor="sheet-date">
              {t("matchDate")}
            </label>
            <input
              id="sheet-date"
              type="date"
              value={header.date ?? ""}
              onChange={(e) => setHeader({ date: e.target.value || null })}
              className={`input w-full ${warn("date")}`}
              data-testid="sheet-date"
            />
            <div>{sheetFields((s) => s.header.date)}</div>
            {crossValues("date") && (
              <p className="text-xs text-orange-700">
                {t("matchSheetImport.chip.cross_sheet")}
              </p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium" htmlFor="sheet-time">
              {t("kickoffTime")}
            </label>
            <input
              id="sheet-time"
              type="time"
              value={header.time ?? ""}
              onChange={(e) => setHeader({ time: e.target.value || null })}
              className={`input w-full ${warn("time")}`}
              data-testid="sheet-time"
            />
            <div>{sheetFields((s) => s.header.time)}</div>
            {crossValues("time") && (
              <p className="text-xs text-orange-700">
                {t("matchSheetImport.chip.cross_sheet")}
              </p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium" htmlFor="sheet-season">
              {t("season", { defaultValue: "Season" })}
            </label>
            <input
              id="sheet-season"
              type="text"
              value={header.season ?? ""}
              onChange={(e) => setHeader({ season: e.target.value })}
              className={`input w-full ${warn("season")}`}
              data-testid="sheet-season"
            />
            <div>{sheetFields((s) => s.header.season)}</div>
          </div>
          <div>
            <label className="block text-sm font-medium" htmlFor="sheet-venue">
              {t("venue")}
            </label>
            <select
              id="sheet-venue"
              value={header.venueId ?? "__unresolved__"}
              onChange={(e) => {
                if (e.target.value !== "__unresolved__")
                  setHeader({ venueId: e.target.value });
              }}
              className={`input w-full ${warn("venue")}`}
              data-testid="sheet-venue"
            >
              <option value="__unresolved__" disabled>
                {t("matchSheetImport.choose")}
              </option>
              <option value="">{t("matchSheetImport.noVenue")}</option>
              {venues.map((v) => (
                <option key={v.venue_id} value={v.venue_id}>
                  {v.name}
                  {v.city ? ` - ${v.city}` : ""}
                </option>
              ))}
            </select>
            <div>{sheetFields((s) => s.header.venue)}</div>
          </div>
          <div className="md:col-span-2">
            <label
              className="block text-sm font-medium"
              htmlFor="sheet-competition"
            >
              {t("competitions")} *
            </label>
            <select
              id="sheet-competition"
              value={header.competitionId ?? "__unresolved__"}
              onChange={(e) => {
                if (e.target.value !== "__unresolved__")
                  setHeader({ competitionId: e.target.value });
              }}
              className={`input w-full ${warn("competition")}`}
              data-testid="sheet-competition"
            >
              <option value="__unresolved__" disabled>
                {t("matchSheetImport.choose")}
              </option>
              {competitions.map((c) => (
                <option
                  key={String(c.competition_id)}
                  value={String(c.competition_id)}
                >
                  {c.name}
                </option>
              ))}
            </select>
            <div>
              {sheetFields((s) => s.header.tournament)}
              {sheetFields((s) => s.header.category)}
            </div>
          </div>
        </div>
      </section>

      {/* Teams, home/away, kits */}
      <section
        className="space-y-3 rounded border p-4"
        data-testid="sheet-teams"
      >
        <h4 className="font-semibold">{t("matchSheetImport.teamsTitle")}</h4>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {([0, 1] as const).map((i) => {
            const s = review.sheets[i];
            const sheet = ex.sheets[i];
            return (
              <div
                key={i}
                className="space-y-2"
                data-testid={`sheet-team-${i}`}
              >
                <label
                  className="block text-sm font-medium"
                  htmlFor={`sheet-team-select-${i}`}
                >
                  {t("matchSheetImport.sheetN", { n: i + 1 })}:{" "}
                  {raw(sheet.header.team)}
                </label>
                <select
                  id={`sheet-team-select-${i}`}
                  value={s.teamId ?? ""}
                  onChange={(e) => handleTeamChange(i, e.target.value)}
                  className={`input w-full ${
                    hasConflict(`team-${i}`) || hasConflict("team-same")
                      ? "border-red-500 bg-red-50"
                      : ""
                  }`}
                  data-testid={`sheet-team-select-${i}`}
                >
                  <option value="">{t("selectTeam")}</option>
                  {teams.map((tm) => (
                    <option key={tm.team_id} value={tm.team_id}>
                      {tm.name}
                    </option>
                  ))}
                </select>
                <div className="flex items-center gap-2 text-xs">
                  <span
                    className="rounded bg-gray-100 px-1.5 py-0.5"
                    data-testid={`sheet-team-status-${i}`}
                  >
                    {t(`matchSheetImport.chip.${sheet.team_match.status}`)}
                  </span>
                  <button
                    type="button"
                    className="btn btn-sm btn-secondary"
                    disabled={!s.teamId || s.teamConfirmed}
                    data-testid={`sheet-team-confirm-${i}`}
                    onClick={() =>
                      setReview((prev) =>
                        prev
                          ? {
                              ...prev,
                              sheets: prev.sheets.map((x, k) =>
                                k === i ? { ...x, teamConfirmed: true } : x,
                              ) as ReviewState["sheets"],
                            }
                          : prev,
                      )
                    }
                  >
                    {s.teamConfirmed
                      ? t("matchSheetImport.confirmed")
                      : t("matchSheetImport.confirmTeam")}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
        {hasConflict("team-same") && (
          <p className="text-sm text-red-700">
            {t("matchSheetImport.sameTeam")}
          </p>
        )}

        <div data-testid="sheet-home-away" className="space-y-1">
          <p className="text-sm font-medium">
            {t("matchSheetImport.homeAwayTitle")}
          </p>
          {([0, 1] as const).map((i) => (
            <label key={i} className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="sheet-home"
                checked={review.homeIndex === i}
                onChange={() =>
                  setReview((prev) =>
                    prev
                      ? { ...prev, homeIndex: i, homeConfirmed: false }
                      : prev,
                  )
                }
                data-testid={`sheet-home-radio-${i}`}
              />
              {t("matchSheetImport.isHome", {
                team: teamName(review.sheets[i].teamId),
              })}
              {ex.home_suggestion === i && (
                <span className="rounded bg-blue-100 px-1.5 text-xs text-blue-800">
                  {t("matchSheetImport.suggested")}
                </span>
              )}
            </label>
          ))}
          <button
            type="button"
            className="btn btn-sm btn-secondary"
            disabled={review.homeIndex == null || review.homeConfirmed}
            data-testid="sheet-home-confirm"
            onClick={() =>
              setReview((prev) =>
                prev ? { ...prev, homeConfirmed: true } : prev,
              )
            }
          >
            {review.homeConfirmed
              ? t("matchSheetImport.confirmed")
              : t("matchSheetImport.confirmHomeAway")}
          </button>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {([0, 1] as const).map((i) => (
            <div key={i} className="space-y-2" data-testid={`sheet-kit-${i}`}>
              <p className="text-sm font-medium">
                {t("matchSheetImport.kitTitle", {
                  team: teamName(review.sheets[i].teamId),
                })}
              </p>
              {KIT_PARTS.map((part) => {
                const field = ex.sheets[i].header.kit[part];
                const eff = effectiveKitHex(review.sheets[i], field, part);
                return (
                  <div key={part} className="flex flex-wrap items-center gap-2">
                    <span className="text-xs text-gray-400">
                      ({raw(field)})
                    </span>
                    <KitColorPicker
                      value={eff ?? null}
                      invalid={eff === undefined}
                      testId={`sheet-kit-${i}-${part}`}
                      onChange={(hex) =>
                        setReview((prev) =>
                          prev
                            ? {
                                ...prev,
                                sheets: prev.sheets.map((x, k) =>
                                  k === i
                                    ? {
                                        ...x,
                                        kitEdits: {
                                          ...x.kitEdits,
                                          [part]: hex,
                                        },
                                      }
                                    : x,
                                ) as ReviewState["sheets"],
                              }
                            : prev,
                        )
                      }
                    />
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </section>

      {/* Line-ups */}
      {([0, 1] as const).map((i) => {
        const rows = review.sheets[i].rows;
        const dups = duplicateNumberKeys(review.sheets[i]);
        const clashes = sheetRosterClashes(
          review.sheets[i],
          rosterCtx?.rosters[i] ?? null,
        );
        const dupGroups = duplicatePlayerGroups(review.sheets[i]);
        const duplicateRows = new Set(dupGroups.flatMap((g) => g.rowKeys));
        const rowLabel = (key: string) => {
          const r = rows.find((x) => x.key === key);
          return r
            ? `${t(r.section === "starters" ? "starters" : "substitutes")} #${r.source.row}`
            : key;
        };
        const teamId = review.sheets[i].teamId;
        const prefix = `${i}:`;
        const slice = (src: Record<string, string>) =>
          Object.fromEntries(
            Object.entries(src)
              .filter(([k, v]) => k.startsWith(prefix) && v)
              .map(([k, v]) => [k.slice(prefix.length), v]),
          );
        const rematchingHere = new Set(
          [...rematching]
            .filter((k) => k.startsWith(prefix))
            .map((k) => k.slice(prefix.length)),
        );
        const createErrors =
          continueError?.rowKey && continueError.sheetIndex === i
            ? { [continueError.rowKey]: continueError.message }
            : {};
        const common = {
          sheetIndex: i,
          duplicates: dups,
          teamReady: !!teamId,
          rematching: rematchingHere,
          errors: slice(rowErrors),
          createErrors,
          clashes,
          duplicateRows,
          onSection: (key: string, section: RowSection) =>
            handleSection(i, key, section),
          onEdit: (key: string, edits: RowEdits) => handleEdit(i, key, edits),
          onNameCommit: (key: string, pending?: RowEdits) =>
            handleNameCommit(i, key, pending),
          onResolve: (key: string, r: RowResolution) =>
            handleResolve(i, key, r),
        };
        return (
          <section
            key={i}
            className="space-y-3 rounded border p-4"
            data-testid={`sheet-lineup-${i}`}
          >
            <h4 className="font-semibold">
              {t("matchSheetImport.sheetN", { n: i + 1 })} -{" "}
              {teamName(review.sheets[i].teamId)}
            </h4>
            {teamId && rosterCtx?.failed[i] && (
              <div
                className="rounded border border-red-300 bg-red-50 p-2 text-sm text-red-700"
                role="alert"
                data-testid={`sheet-roster-error-${i}`}
              >
                {t("matchSheetImport.rosterLoadFailed")}{" "}
                <button
                  type="button"
                  className="underline"
                  data-testid={`sheet-roster-retry-${i}`}
                  onClick={() => retryRoster(teamId)}
                >
                  {t("matchSheetImport.rosterRetry")}
                </button>
              </div>
            )}
            {dupGroups.length > 0 && (
              <ul
                className="list-disc pl-5 text-sm text-red-700"
                data-testid={`sheet-duplicates-${i}`}
              >
                {dupGroups.map((g) => (
                  <li key={g.playerId}>
                    {t("matchSheetImport.duplicatePlayer", {
                      rows: g.rowKeys.map(rowLabel).join(" / "),
                    })}
                  </li>
                ))}
              </ul>
            )}
            <h5 className="text-sm font-medium">{t("starters")}</h5>
            <SheetRowsTable
              {...common}
              rows={rows.filter((r) => r.section === "starters")}
            />
            <h5 className="text-sm font-medium">{t("substitutes")}</h5>
            <SheetRowsTable
              {...common}
              rows={rows.filter((r) => r.section === "substitutes")}
            />
            <div
              className="text-sm text-gray-600"
              data-testid={`sheet-staff-${i}`}
            >
              <p className="font-medium">{t("matchSheetImport.staffTitle")}</p>
              <ul className="list-disc pl-5">
                {ex.sheets[i].staff.map((m, k) => (
                  <li key={k}>
                    {raw(m.role)}:{" "}
                    {[raw(m.first_name), raw(m.surname_1), raw(m.surname_2)]
                      .filter((p) => p !== "-")
                      .join(" ")}
                  </li>
                ))}
              </ul>
              <p>
                {t("matchSheetImport.delegate")}: {raw(ex.sheets[i].delegate)}
              </p>
              <p className="text-xs text-gray-400">
                {t("matchSheetImport.notSaved")}
              </p>
            </div>
          </section>
        );
      })}

      <div className="flex items-center justify-between border-t pt-4">
        <button
          type="button"
          className="btn btn-secondary"
          onClick={handleCancel}
          data-testid="sheet-import-cancel"
        >
          {t("cancel")}
        </button>
        <div className="flex flex-col items-end gap-2">
          {continueError && (
            <div
              ref={continueErrRef}
              className="max-w-md rounded border border-red-300 bg-red-50 p-2 text-sm text-red-700"
              role="alert"
              data-testid="sheet-import-continue-error"
            >
              {continueError.message}
            </div>
          )}
          {conflicts.some((c) => c.scope === "roster") && (
            <span
              className="text-xs text-red-700"
              data-testid="sheet-import-continue-reason"
            >
              {t("matchSheetImport.continueBlockedRoster")}
            </span>
          )}
          <div className="flex items-center gap-3">
            <span
              className={`text-sm ${conflicts.length ? "text-red-700" : "text-green-700"}`}
              data-testid="sheet-import-counter"
            >
              {conflicts.length
                ? t("matchSheetImport.remaining", { count: conflicts.length })
                : t("matchSheetImport.allResolved")}
            </span>
            <button
              type="button"
              className="btn btn-primary"
              disabled={conflicts.length > 0 || continuing}
              onClick={handleContinue}
              data-testid="sheet-import-continue"
            >
              {continuing
                ? t("matchSheetImport.continuing")
                : t("matchSheetImport.continue")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
